import { App } from "antd";
import { useMutation } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import api from "../../utils/api";
import { userAtom } from "../../utils/store";

interface UploadRequiredDocumentPayload {
  requiredDocumentId: number;
  file: File;
  originalFileName?: string;
}

const uploadRequiredDocument = async (payload: FormData) => {
  return (
    await api.post("/AdmissionDocument/Student/UploadDocument", payload, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  ).data as Response;
};

export const useUploadRequiredDocumentMutation = () => {
  const { notification } = App.useApp();
  const user = useAtomValue(userAtom);

  const mutation = useMutation({
    mutationFn: uploadRequiredDocument,
  });

  const uploadHandler = async ({
    requiredDocumentId,
    file,
    originalFileName,
  }: UploadRequiredDocumentPayload) => {
    if (!user?.applicantId) {
      const error = new Error("Applicant ID is missing");
      notification.error({
        message: "Error",
        description: "Unable to upload document because applicant details are missing.",
      });
      throw error;
    }

    const formData = new FormData();
    const resolvedOriginalFileName = originalFileName || file.name;
    const fileExtension = file.name.includes(".")
      ? `.${file.name.split(".").pop()?.toLowerCase()}`
      : "";

    formData.append("ApplicantId", user.applicantId);
    formData.append("RequiredDocumentId", `${requiredDocumentId}`);
    formData.append("File", file);
    formData.append("InstitutionShortName", user.institutionShortName ?? "");
    formData.append("OriginalFileName", resolvedOriginalFileName);
    formData.append("FileSizeInBytes", `${file.size}`);
    formData.append("FileExtension", fileExtension);

    try {
      const response = await mutation.mutateAsync(formData);

      notification.success({
        message: "Success",
        description: response?.message || "Document uploaded successfully",
      });

      return response;
    } catch (error: any) {
      notification.error({
        message: "Error",
        description:
          error?.response?.data?.message || "Failed to upload document",
      });
      throw error;
    }
  };

  return {
    uploadHandler,
    isLoading: mutation.isPending,
  };
};

export default useUploadRequiredDocumentMutation;
